
import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { signInWithEmail } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Probeer eerst in te loggen
      await signInWithEmail(email, password);
      
      // Wacht even om zeker te zijn dat de auth state is bijgewerkt
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Controleer dan of de gebruiker admin rechten heeft
      const { data: isAdmin, error: adminCheckError } = await supabase
        .rpc('is_admin_secure', { 
          input_user_id: (await supabase.auth.getUser()).data.user?.id 
        });

      if (adminCheckError) {
        console.error('Admin check error:', adminCheckError);
        throw new Error("Fout bij controleren van admin rechten");
      }

      if (!isAdmin) {
        throw new Error("Geen admin rechten");
      }

      // Als alles goed is, navigeer naar het admin dashboard of de opgevraagde pagina
      const from = location.state?.from || "/admin/dashboard";
      
      toast({
        title: "Succesvol ingelogd",
        description: "Welkom in het admin panel",
      });
      
      navigate(from);
    } catch (error: any) {
      console.error('Login error:', error);
      
      let errorMessage = "Er ging iets mis bij het inloggen";
      
      if (error.message === "Invalid login credentials") {
        errorMessage = "Ongeldige e-mail of wachtwoord";
      } else if (error.message === "Geen admin rechten") {
        errorMessage = "Je hebt geen toegang tot het admin panel";
      }
      
      toast({
        title: "Fout bij inloggen",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-full max-w-md space-y-8 p-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold">Admin Login</h1>
          <p className="text-muted-foreground mt-2">
            Log in om toegang te krijgen tot het admin panel
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Input
              type="email"
              placeholder="E-mailadres"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>
          <div>
            <Input
              type="password"
              placeholder="Wachtwoord"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? "Bezig met inloggen..." : "Inloggen"}
          </Button>
        </form>
      </div>
    </div>
  );
}
